import type { DosagePath, DosagePlan, DtpProductResponse, PublicIntakeProgram } from '@bloom/shared';

export interface FamilyDosagePlan extends DosagePlan {
  programId: string;
  programName: string;
}

/**
 * Dosage plans from every active intake program that points at this family's
 * SKUs (a family like semaglutide spans separate injection and oral-tablet
 * programs), grouped by program with starter plans first.
 */
export function familyProgramDosing(
  programs: PublicIntakeProgram[],
  familyProducts: Pick<DtpProductResponse, '_id'>[]
): FamilyDosagePlan[] {
  const ids = new Set(familyProducts.map(p => p._id));
  const order = (p: DosagePlan) => (p.path === 'starter' ? 0 : 1);
  return programs.flatMap(program => program.active
    ? (program.dosagePlans || [])
      .filter(p => ids.has(p.productId))
      .map(p => ({ ...p, programId: program.id, programName: program.name }))
      .sort((a, b) => order(a) - order(b))
    : []);
}

/** Plan name without the "(starter)" / "(continuation)" suffix the section or chip already shows. */
export const planDisplayName = (p: Pick<DosagePlan, 'name'>) => p.name.replace(/\s*\((starter|continuation)\)\s*$/i, '');

// Plan ids are only unique within a program.
export const planKey = (p: Pick<FamilyDosagePlan, 'programId' | 'id'>) => `${p.programId}:${p.id}`;

export interface DosingOption {
  key: string;
  /** The plan this option selects (the starter plan when paths are merged). */
  plan: FamilyDosagePlan;
  /** Paths this option covers; both when starter and continuation are identical. */
  paths: DosagePath[];
  /** False when the patient's known state is outside the plan's allowedStates. */
  available: boolean;
  /** The plan's own state restriction, when it has one and the patient's state is unknown. */
  limitedTo?: string[];
}
export interface DosingSection {
  title?: string;
  /** Sections are per program, so single-path options need their own starting/continuing tag. */
  byProgram: boolean;
  options: DosingOption[];
}

const PATH_TITLES: Record<DosagePath, string> = {
  starter: 'Starting treatment',
  continuation: 'Continuing treatment',
};

/**
 * Shape family plans for display:
 *  - a program whose starter and continuation plans are identical (same SKU
 *    and dose) lists each dose once instead of twice;
 *  - plans restricted to states the clinic can't ship to are dropped, and
 *    plans restricted away from the patient's known state are marked
 *    unavailable;
 *  - one program splits into Starting / Continuing sections, several programs
 *    get a section each.
 */
export function familyDosingSections(
  plans: FamilyDosagePlan[],
  { clinicStates = [], patientState }: { clinicStates?: string[]; patientState?: string } = {}
): DosingSection[] {
  const shippable = plans.filter(p => !p.allowedStates?.length || !clinicStates.length || p.allowedStates.some(s => clinicStates.includes(s)));
  const option = (plan: FamilyDosagePlan, paths: DosagePath[]): DosingOption => {
    const restricted = !!plan.allowedStates?.length;
    return {
      key: planKey(plan),
      plan,
      paths,
      available: !restricted || !patientState || plan.allowedStates!.includes(patientState),
      limitedTo: restricted && !patientState ? plan.allowedStates : undefined,
    };
  };
  const programIds = [...new Set(shippable.map(p => p.programId))];
  const byProgram = programIds.map(id => {
    const own = shippable.filter(p => p.programId === id);
    const twin = (p: FamilyDosagePlan) => own.some(o => o !== p && o.path !== p.path && o.productId === p.productId && o.doseLabel === p.doseLabel && sameStates(o, p));
    const merged = own.every(twin);
    return {
      name: own[0].programName,
      merged,
      options: merged
        ? own.filter(p => p.path === 'starter').map(p => option(p, ['starter', 'continuation']))
        : own.map(p => option(p, [p.path])),
    };
  });
  if (byProgram.length === 1) {
    const [only] = byProgram;
    if (only.merged) return [{ byProgram: false, options: only.options }];
    return (['starter', 'continuation'] as const)
      .map(path => ({ title: PATH_TITLES[path], byProgram: false, options: only.options.filter(o => o.paths[0] === path) }))
      .filter(s => s.options.length);
  }
  return byProgram.map(p => ({ title: p.name, byProgram: true, options: p.options }));
}

const sameStates = (a: DosagePlan, b: DosagePlan) =>
  (a.allowedStates || []).slice().sort().join() === (b.allowedStates || []).slice().sort().join();
